// One standard contract for the chat agent to open any native editor AI popup
// pre-filled. See docs/superpowers/specs/2026-06-05-agent-popup-gateway-design.md.
// Plain data + functions — no classes, no registry object graph.

import { useEffect } from "react";

// FIX 1: Toggle the `aidea-popup-docked` class on document.body and broadcast
// a window event so ChatPanel can listen and switch to slim-rail mode.
export function setPopupDocked(on) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle("aidea-popup-docked", !!on);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("aidea:popupDocked", { detail: { docked: !!on } }));
  }
}

// FIX 2: After a popup opens, briefly highlight its primary action button.
// Queries for a visible, enabled button matching common generate-verb patterns,
// adds the `aidea-glance` class for 1800ms, then removes it. No-op when none found.
export function glancePrimaryButton() {
  if (typeof document === "undefined") return;
  const RE = /generate|regenerate|oluştur|üret/i;
  const buttons = document.querySelectorAll("button:not([disabled])");
  let target = null;
  for (let i = 0; i < buttons.length; i++) {
    const btn = buttons[i];
    const text = btn.textContent || "";
    if (!RE.test(text)) continue;
    const rect = btn.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) continue;
    target = btn;
    break;
  }
  if (!target) return;
  target.classList.add("aidea-glance");
  setTimeout(() => {
    if (target) target.classList.remove("aidea-glance");
  }, 1800);
}

export const AI_POPUP_EVENT = "aidea:openAIPopup";

// feature → how EditorModals opens that popup. Add a row to onboard a feature.
export const FEATURE_REGISTRY = {
  image: { stateKey: "showAddImageModal" },
  video: { stateKey: "showAddVideoModal" },
  infographicScene: { stateKey: "showInfographicSceneModal" },
  quiz: { stateKey: "showQuestionGeneratorModal" },
  sound: { stateKey: "showAddSoundModal", initialTab: "ai" },
  avatar: { stateKey: "showAddSoundModal", initialTab: "avatar" },
  editImage: { stateKey: "showImageAnimatorModal" },
};

export function isKnownFeature(feature) {
  return (
    typeof feature === "string" && Object.prototype.hasOwnProperty.call(FEATURE_REGISTRY, feature)
  );
}

// Stash keyed by feature so a popup that mounts AFTER the event still gets it.
function stashPayload(payload) {
  if (typeof window === "undefined" || !isKnownFeature(payload?.feature)) return;
  window.__aideaPendingAIPopup = window.__aideaPendingAIPopup || {};
  window.__aideaPendingAIPopup[payload.feature] = payload;
}

// Read + clear the stashed payload for a feature (one-shot).
export function drainPendingPayload(feature) {
  if (typeof window === "undefined" || !window.__aideaPendingAIPopup) return null;
  const pending = window.__aideaPendingAIPopup[feature] || null;
  if (pending) window.__aideaPendingAIPopup[feature] = null;
  return pending;
}

// Fire (and re-fire at 0/500/1100ms) the open event; stash first so a late-
// mounting popup drains it. Returns a cleanup that cancels the pending re-fires.
// FIX 2: schedules glancePrimaryButton ~1s after the first fire.
export function dispatchOpenAIPopup(payload) {
  if (typeof window === "undefined" || !isKnownFeature(payload?.feature)) {
    return () => {};
  }
  stashPayload(payload);
  const fire = () => window.dispatchEvent(new CustomEvent(AI_POPUP_EVENT, { detail: payload }));
  fire();
  const t1 = setTimeout(fire, 500);
  const t2 = setTimeout(fire, 1100);
  const tGlance = setTimeout(glancePrimaryButton, 1100);
  return () => {
    clearTimeout(t1);
    clearTimeout(t2);
    clearTimeout(tGlance);
  };
}

// Auto-open-once guard. Each tool-UI card auto-opens its popup the FIRST time
// its tool call completes, but must NOT re-open on remount — closing/reopening
// the chat, OR restoring a saved conversation (even after a browser restart),
// remounts every card and would otherwise re-fire every past popup at once. We
// record which tool calls have already auto-opened, keyed by a stable id (the
// tool-call id, with a content fallback). Stored in localStorage (NOT session):
// conversations themselves persist in localStorage and restore across browser
// restarts, so the "already opened" record must persist alongside them or a
// restored conversation re-pops all its popups. Capped to the most-recent N
// keys. Returns true the first time a key is seen (and records it), else false.
const AUTO_OPEN_KEY = "aidea:autoOpenedPopups";
const AUTO_OPEN_MAX = 300;
let _autoOpened = null;
function autoOpenedSet() {
  if (_autoOpened) return _autoOpened;
  try {
    _autoOpened = new Set(JSON.parse(localStorage.getItem(AUTO_OPEN_KEY) || "[]"));
  } catch {
    _autoOpened = new Set();
  }
  return _autoOpened;
}
export function shouldAutoOpenOnce(key) {
  if (typeof window === "undefined" || !key) return false;
  const set = autoOpenedSet();
  if (set.has(key)) return false;
  set.add(key);
  let arr = [...set];
  if (arr.length > AUTO_OPEN_MAX) {
    arr = arr.slice(arr.length - AUTO_OPEN_MAX);
    _autoOpened = new Set(arr);
  }
  try {
    localStorage.setItem(AUTO_OPEN_KEY, JSON.stringify(arr));
  } catch {
    /* localStorage full / unavailable — best-effort */
  }
  return true;
}

// Hook a popup's AI-input component uses to apply the agent's intent on open.
// `applyFn` receives the full payload { feature, prompt, settings, target }.
// IMPORTANT: memoize applyFn (useCallback) at the call site or the effect re-runs.
export function usePopupPrefill(feature, applyFn) {
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const apply = (payload) => {
      if (!payload || payload.feature !== feature) return;
      try {
        applyFn(payload);
      } catch {
        // Prefill is best-effort; never let it break the popup.
      }
    };
    const handler = (e) => apply(e?.detail);
    window.addEventListener(AI_POPUP_EVENT, handler);
    const pending = drainPendingPayload(feature);
    if (pending) apply(pending);
    return () => window.removeEventListener(AI_POPUP_EVENT, handler);
  }, [feature, applyFn]);
}
